import { useMutation } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { SteersCredentialsPageDataQuery } from "generated-graphql/graphql";
import { useState } from "react";
import { AddCredentialAccessDialog } from "./AddCredentialAccessDialog";
import {
  CREATE_ALTERNATE_ID_INFO,
  DELETE_ALTERNATE_ID_INFO,
  REMOVE_STEERS_CREDENTIAL_ACCESS,
  UPDATE_ALTERNATE_ID_INFO,
} from "./api";
import { CnForm, CnFormData } from "./CnForm";
import { isValidCn } from "./utils";

type Mapping = SteersCredentialsPageDataQuery["steersCredentialsPageData"]["mappings"][number];
type Credential = SteersCredentialsPageDataQuery["steersCredentialsPageData"]["credentials"][number];

interface EditCnDialogProps {
  mapping: Mapping;
  credentials: Credential[];
  existingMappings: Mapping[];
  onClose: () => void;
  onSuccess: () => void;
  /** Called when data should refresh but dialog stays open */
  onRefresh: () => void;
}

export function EditCnDialog({
  mapping,
  credentials,
  existingMappings,
  onClose,
  onSuccess,
  onRefresh,
}: EditCnDialogProps): JSX.Element {
  const alerts = useAlerts();
  const info = mapping.alternateIdInfo;
  const isNewRecord = !info.id;

  const [formData, setFormData] = useState<CnFormData>({
    cnValue: info.alternateIdValue,
    tenantId: info.tenantId || null,
    description: info.description ?? "",
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddCredentialDialog, setShowAddCredentialDialog] = useState(false);
  const [removeCredentialConfirm, setRemoveCredentialConfirm] = useState<{
    credentialId: string;
    credentialUsername: string;
  } | null>(null);

  const [createAlternateIdInfo, { loading: creating }] = useMutation(
    CREATE_ALTERNATE_ID_INFO,
    {
      onCompleted: () => {
        alerts.success("CN info saved successfully");
        onSuccess();
      },
      onError: (error) => {
        alerts.error("Failed to save CN info", error);
      },
    }
  );

  const [updateAlternateIdInfo, { loading: updating }] = useMutation(
    UPDATE_ALTERNATE_ID_INFO,
    {
      onCompleted: () => {
        alerts.success("CN updated successfully");
        onSuccess();
      },
      onError: (error) => {
        alerts.error("Failed to update CN", error);
      },
    }
  );

  const [deleteAlternateIdInfo, { loading: deleting }] = useMutation(
    DELETE_ALTERNATE_ID_INFO,
    {
      onCompleted: () => {
        alerts.success("CN deleted successfully");
        onSuccess();
      },
      onError: (error) => {
        alerts.error("Failed to delete CN", error);
      },
    }
  );

  const [removeCredentialAccess, { loading: removingAccess }] = useMutation(
    REMOVE_STEERS_CREDENTIAL_ACCESS,
    {
      onCompleted: () => {
        alerts.success("Credential access removed");
        setRemoveCredentialConfirm(null);
        onRefresh();
      },
      onError: (error) => {
        alerts.error("Failed to remove credential access", error);
      },
    }
  );

  // Get the list of credentials that have access to this CN
  const associatedCredentials = mapping.associatedCredentialIds
    .map((credentialId) => {
      const credential = credentials.find((c) => c.id === credentialId);
      return credential
        ? { id: credential.id, username: credential.username, name: credential.name }
        : null;
    })
    .filter((c): c is NonNullable<typeof c> => c !== null);

  function handleSubmit(): void {
    if (!formData.tenantId) {
      return;
    }

    const input = {
      alternateIdValue: formData.cnValue,
      tenantId: formData.tenantId,
      description: formData.description || undefined,
    };

    if (isNewRecord) {
      createAlternateIdInfo({ variables: { input } });
    } else {
      updateAlternateIdInfo({ variables: { id: info.id, input } });
    }
  }

  function handleDelete(): void {
    deleteAlternateIdInfo({
      variables: { id: info.id },
    });
  }

  function handleRemoveCredentialAccess(): void {
    if (!removeCredentialConfirm) return;
    removeCredentialAccess({
      variables: {
        credentialId: removeCredentialConfirm.credentialId,
        alternateIdValue: info.alternateIdValue,
      },
    });
  }

  const isValid = isValidCn(formData.cnValue) && formData.tenantId;
  const hasCredentialAssociations = mapping.credentialCount > 0;

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        title={isNewRecord ? "Add CN Info" : "Edit CN"}
        maxWidth="md"
        primaryActionButtonProps={{
          onClick: handleSubmit,
          loading: creating || updating,
          disabled: !isValid,
          saveText: isNewRecord ? "Save" : "Save",
        }}
        secondaryActionElements={
          isNewRecord
            ? []
            : [
                <Button
                  key="delete"
                  color="error"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={hasCredentialAssociations}
                >
                  Delete
                </Button>,
              ]
        }
      >
        <CnForm data={formData} onChange={setFormData} />

        {isNewRecord && (
          <Alert severity="info" sx={{ mt: 3 }}>
            This CN exists in credential associations but has no tenant or
            description info. Add details to complete the record.
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />

        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2">Associated Credentials</Typography>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => setShowAddCredentialDialog(true)}
            >
              Add Credential
            </Button>
          </Stack>

          {associatedCredentials.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No credentials have access to this CN
            </Typography>
          ) : (
            <List dense disablePadding>
              {associatedCredentials.map((credential) => (
                <ListItem
                  key={credential.id}
                  secondaryAction={
                    <Tooltip title="Remove access">
                      <IconButton
                        edge="end"
                        size="small"
                        color="error"
                        onClick={() =>
                          setRemoveCredentialConfirm({
                            credentialId: credential.id,
                            credentialUsername: credential.username,
                          })
                        }
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 0.5,
                  }}
                >
                  <ListItemText
                    primary={credential.username}
                    secondary={credential.name}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {!isNewRecord && hasCredentialAssociations && (
          <Alert severity="info" sx={{ mt: 3 }}>
            Cannot delete CN while credential associations exist. Remove all
            associations first.
          </Alert>
        )}
      </Dialog>

      <ConfirmDialog
        open={showDeleteConfirm}
        header="Delete CN"
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        loading={deleting}
        confirmText="Delete"
      >
        Are you sure you want to delete CN <strong>{info.alternateIdValue}</strong>?
      </ConfirmDialog>

      <ConfirmDialog
        open={!!removeCredentialConfirm}
        header="Remove Credential Access"
        onClose={() => setRemoveCredentialConfirm(null)}
        onConfirm={handleRemoveCredentialAccess}
        loading={removingAccess}
        confirmText="Remove"
      >
        Remove credential{" "}
        <strong>{removeCredentialConfirm?.credentialUsername}</strong> access
        from CN <strong>{info.alternateIdValue}</strong>?
      </ConfirmDialog>

      {showAddCredentialDialog && (
        <AddCredentialAccessDialog
          existingMappings={existingMappings}
          initialCnValue={info.alternateIdValue}
          onClose={() => setShowAddCredentialDialog(false)}
          onSuccess={() => {
            setShowAddCredentialDialog(false);
            onRefresh();
          }}
        />
      )}
    </>
  );
}
